import { useState, useEffect, useCallback, useRef } from 'react';

import { useGeoMarkupStore } from '@/store';
import {
  selectIsAuthenticated,
  selectIsCheckingAuth,
} from '@/store/selectors/authSelectors';
import { invitationsService } from '@/services/api';
import {
  savePendingInvitation,
  clearPendingInvitation,
} from '@/utils/pendingInvitation';
import { buildAuthorizeUrl } from '@/utils/authRedirect';
import { emailsMatch } from '@/utils/helpers';
import type { ApiError } from '@/types/api';
import type {
  GetInvitationByTokenResponse,
  AcceptInvitationResponse,
} from '@/types/api/responses';

export type InvitationStep =
  | 'loading'
  | 'details'
  | 'profile'
  | 'success'
  | 'declined'
  | 'error';

export type InvitationErrorType =
  | 'not_found'
  | 'expired'
  | 'already_accepted'
  | 'revoked'
  | 'email_mismatch'
  | 'already_member'
  | 'network_error';

export interface UseInvitationFlowReturn {
  step: InvitationStep;
  invitation: GetInvitationByTokenResponse | null;
  acceptResult: AcceptInvitationResponse | null;
  errorType: InvitationErrorType | null;
  isAccepting: boolean;
  isDeclining: boolean;
  isAuthenticated: boolean;
  handleAccept: () => Promise<void>;
  handleDecline: () => Promise<void>;
  handleRetry: () => void;
  handleLogout: () => void;
}

const BACKEND_ERROR_INVITATION_EMAIL_MISMATCH = 'INVITATION_EMAIL_MISMATCH';

function mapGoneError(apiError: ApiError): InvitationErrorType {
  const message = apiError.message.toLowerCase();
  if (message.includes('accepted')) return 'already_accepted';
  if (message.includes('revoked')) return 'revoked';
  return 'expired';
}

function mapFetchError(apiError: ApiError): InvitationErrorType {
  if (apiError.code === 'NOT_FOUND') return 'not_found';
  if (apiError.status === 410) return mapGoneError(apiError);
  return 'network_error';
}

function mapActionError(apiError: ApiError): InvitationErrorType {
  if (apiError.code === 'CONFLICT') return 'already_member';
  if (apiError.backendErrorCode === BACKEND_ERROR_INVITATION_EMAIL_MISMATCH) {
    return 'email_mismatch';
  }
  if (apiError.status === 410) return mapGoneError(apiError);
  return 'network_error';
}

function redirectToAuth(action: string): void {
  const returnPath = `${window.location.pathname}?action=${action}`;
  window.location.href = buildAuthorizeUrl(returnPath);
}

export function useInvitationFlow(token: string): UseInvitationFlowReturn {
  const isAuthenticated = useGeoMarkupStore(selectIsAuthenticated);
  const isCheckingAuth = useGeoMarkupStore(selectIsCheckingAuth);

  const [step, setStep] = useState<InvitationStep>('loading');
  const [invitation, setInvitation] =
    useState<GetInvitationByTokenResponse | null>(null);
  const [acceptResult, setAcceptResult] =
    useState<AcceptInvitationResponse | null>(null);
  const [errorType, setErrorType] = useState<InvitationErrorType | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const autoAcceptTriggeredRef = useRef(false);

  const fetchInvitation = useCallback(async () => {
    setStep('loading');
    setErrorType(null);
    setInvitation(null);
    setAcceptResult(null);

    try {
      const response = await invitationsService.getByToken(token);
      setInvitation(response.data);

      const authState = useGeoMarkupStore.getState().auth;
      const currentEmail = authState.user?.email ?? null;

      if (
        authState.isAuthenticated &&
        !emailsMatch(response.data.email, currentEmail)
      ) {
        setErrorType('email_mismatch');
        setStep('error');
        return;
      }

      setStep('details');
    } catch (fetchError) {
      const apiError = fetchError as ApiError;
      setErrorType(mapFetchError(apiError));
      setStep('error');
    }
  }, [token]);

  const handleAccept = useCallback(async () => {
    if (!isAuthenticated) {
      savePendingInvitation(token, 'accept');
      redirectToAuth('accept');
      return;
    }

    setIsAccepting(true);
    try {
      const response = await invitationsService.accept(token);
      setAcceptResult(response.data);
      await useGeoMarkupStore.getState().auth.checkAuth();
      useGeoMarkupStore
        .getState()
        .auth.setCurrentWorkspace(response.data.workspaceId);
      setStep('success');
    } catch (acceptError) {
      const apiError = acceptError as ApiError;
      setErrorType(mapActionError(apiError));
      setStep('error');
    } finally {
      setIsAccepting(false);
    }
  }, [token, isAuthenticated]);

  const handleDecline = useCallback(async () => {
    if (!isAuthenticated) {
      savePendingInvitation(token, 'decline');
      redirectToAuth('decline');
      return;
    }

    setIsDeclining(true);
    try {
      await invitationsService.decline(token);
      setStep('declined');
    } catch (declineError) {
      const apiError = declineError as ApiError;
      setErrorType(mapActionError(apiError));
      setStep('error');
    } finally {
      setIsDeclining(false);
    }
  }, [token, isAuthenticated]);

  const handleRetry = useCallback(() => {
    autoAcceptTriggeredRef.current = false;
    fetchInvitation();
  }, [fetchInvitation]);

  const handleLogout = useCallback(() => {
    useGeoMarkupStore.getState().auth.logout();
  }, []);

  useEffect(() => {
    clearPendingInvitation();

    const initialize = async () => {
      await useGeoMarkupStore.getState().auth.checkAuth();
      await fetchInvitation();
    };

    initialize();
  }, [fetchInvitation]);

  useEffect(() => {
    if (
      isCheckingAuth ||
      step !== 'details' ||
      !invitation ||
      autoAcceptTriggeredRef.current
    ) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const action = searchParams.get('action');

    if (action === 'accept' && isAuthenticated) {
      autoAcceptTriggeredRef.current = true;
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      handleAccept();
    }
  }, [isCheckingAuth, step, invitation, isAuthenticated, handleAccept]);

  return {
    step,
    invitation,
    acceptResult,
    errorType,
    isAccepting,
    isDeclining,
    isAuthenticated,
    handleAccept,
    handleDecline,
    handleRetry,
    handleLogout,
  };
}
